/**
 * A mesa única do caso eletrônico (E201.F1, GUS-47, ADR-0144; protótipo 6.8 de 31/10).
 *
 * A pré-análise e a mesa eram duas telas, cada uma com o próprio "próximo passo", e as duas
 * repetiam o estado do exame e o card do pacote. Agora o caso tem um só lugar de trabalho, em
 * `/casos/:id/mesa`:
 * - no alto, a identificação do caso e as ações (casos, conversa, histórico, conferência final);
 * - à esquerda, o índice "Nesta mesa";
 * - no centro, o exame (a prévia real guardada pela preparação, o estado e o card do pacote, uma
 *   vez só), a conclusão (CCB, formalização, conferência no portal e o quadro de cada documento) e
 *   o material (documentos, leitura, fotografias e preparação);
 * - no rodapé fixo, o único próximo passo.
 *
 * As peças são as das duas telas antigas (E74.F3, E78.F4, E91.F2, E162.F2, E168.F7, E172.F7), sem
 * regra nova. O documento editável com o índice das seções do exame é a GUS-145.
 *
 * Sincronia: a conclusão e o pacote mudam → a mesa relê a parte dela e a área do material
 * (`releitura`); o material grava → avisa a mesa (`onGravou`), que relê só a parte dela.
 *
 * E201.F2: "Documentos" abre o original num painel ao lado do exame, e "Ver na fonte" de um campo
 * lido o abre no arquivo e na página citados (`PainelDoDocumentoContexto`). A falha de carga da mesa
 * tem saída: tentar de novo ou voltar aos casos (`FalhaComSaida`, E200.F3).
 *
 * E202.F1 (GUS-145, ADR-0145): o centro mostra o documento do exame (ou, em PARTE, o relatório de
 * risco, pela troca no alto e `?saida=`), seção por seção; o índice lista as seções sob "Exame"; e o
 * PDF guardado pela preparação, a prévia definitiva, abre na aba "Prévia (PDF)" do painel.
 *
 * E202.F2 (ADR-0145 §5–§9): o documento se edita no próprio bloco (`useEdicaoNoDocumento`); salvar
 * relê o documento e o pacote (a prévia em PDF é refeita); sair com edição não salva pergunta antes
 * (`SairSemPerder`); `?trecho=` abre o trecho apontado.
 *
 * E203.F2 (ADR-0146): o clique numa tabela, numa linha ou num texto protegido do documento abre o
 * painel do dado (`PainelDoDadoDaMesa`), que grava pela rota do dado e relê o documento e a prévia.
 *
 * E203.F5 (ADR-0146): o topo é a jornada (Documentos, Prévia, Adicionar documentos, Conferir e
 * assinar com a trava do documento, e o "Mais"); "Protocolo e documentos" só aparece quando o "Mais"
 * ou o endereço (`#protocolo`, o próximo passo "verificar") pede, e fecha pelo próprio botão.
 *
 * E205.F1 (ADR-0147): a mesa em abas. O cabeçalho do caso (sem caminho de pão, com os dados do caso
 * em campos rotulados e as ações Conferir e assinar, Enviar documentos e Mais) e cinco abas — Exame,
 * Pendências, Documentos, Fotografias e facial e Conclusão —, uma visível por vez, escolhidas pelo
 * hash (`useAbaDaMesa`). O índice "Nesta mesa" e o rodapé fixo saíram: a lista inteira do próximo
 * passo é a aba Pendências.
 *
 * E205.F3 (ADR-0147 §5): a aba Pendências lista o que falta, do servidor, com "Resolver" por item, o
 * contador ao lado do nome e o chip "Próximo:" no cabeçalho.
 *
 * E205.F2 (ADR-0147 §2–§3): na aba Exame, uma seção por vez (`useSecaoDoExame`, pelo hash), com o
 * índice ao lado e "Anterior"/"Próxima"; a barra da aba com a troca de saída, o selo do texto e o
 * botão "Exame" (prévia, baixar, ZIP).
 *
 * E205.F4 (ADR-0147 §2.5): a aba Documentos é a lista única; a ficha da leitura saiu da mesa e mora em
 * "Mais → Informações da leitura" — `#ficha-da-leitura` leva para lá.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ArquivosPendentes } from '@/components/ArquivosPendentes/ArquivosPendentes';
import {
  SairSemPerder,
  useDocumentoDaSaida,
  useEdicaoNoDocumento,
} from '@/components/DocumentoDaMesa';
import { pendenciaDoDocumento } from '@/components/DocumentoDaMesa/SeloDoTexto';
import { AbrirDadoContexto } from '@/components/DocumentoDaMesa/pedidoDoDado';
import { ANCORA_DA_FICHA } from '@/components/FichaDaLeitura/AncoraDaFicha';
import { useMesaEletronica } from '@/components/MesaEletronica/useMesaEletronica';
import { useMudancaDasConclusoes } from '@/components/MesaUnica/useMudancaDasConclusoes';
import { AbasDaMesa, ContadorDePendencias, PainelDaAba } from '@/components/MesaUnica/AbasDaMesa';
import { AreaDoExame } from '@/components/MesaUnica/AreaDoExame';
import { AreaDaConclusao } from '@/components/MesaUnica/AreasDaMesa';
import { CabecalhoDaMesa } from '@/components/MesaUnica/CabecalhoDaMesa';
import { dadosDoCaso } from '@/components/MesaUnica/dadosDoCaso';
import { PainelDoDocumento } from '@/components/MesaUnica/PainelDoDocumento';
import {
  PainelDoDadoDaMesa,
  usePainelDoDadoDaMesa,
} from '@/components/MesaUnica/painel/PainelDoDadoDaMesa';
import { useAbaDaMesa } from '@/components/MesaUnica/useAbaDaMesa';
import { usePainelDaMesa, useSaidaDaMesa } from '@/components/MesaUnica/usePainelDaMesa';
import { useFimDaPreparacao } from '@/components/MesaUnica/useFimDaPreparacao';
import { useSecaoDoExame } from '@/components/MesaUnica/useSecaoDoExame';
import {
  PendenciasDaMesa,
  proximoQueBloqueia,
  quantasPendencias,
} from '@/components/Pendencias/PendenciasDaMesa';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { analiseFotograficaEmAndamento } from '@/lib/fotografias_do_caso';
import { PainelDoDocumentoContexto } from '@/lib/painelDoDocumento';
import { resultadoComASaidaQueFalhou } from '@/lib/saidaQueFalhou';
import { usePreparacaoDoPacote } from '@/lib/usePreparacaoDoPacote';
import { useFotografiasDoCaso } from '@/lib/useFotografiasDoCaso';
import { useResultadoDoCaso } from '@/lib/useResultadoDoCaso';
import { MesaEletronicaEstado } from '@/pages/MesaEletronicaStates';
import { PreAnaliseEletronicaPage } from '@/pages/PreAnaliseEletronicaPage';

// E205.F1: sem o índice, o original ao lado divide a largura com as abas a partir de 1024 px.
const COLUNAS_COM_DOCUMENTO = 'lg:grid-cols-[minmax(0,1fr)_minmax(20rem,28rem)]';

export function MesaUnicaPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useApiClient();
  const mesa = useMesaEletronica(caseId);
  const fotografias = useFotografiasDoCaso(client, caseId);
  const [processando, setProcessando] = useState(false);
  // Muda quando a mesa gravou: relê o material e a prévia. O material não avisa de volta.
  const [versao, setVersao] = useState(0);
  // E215.F2 (GUS-315): a conclusão registrada relê também a área do material (o contador "N de M").
  const conclusoesMudaram = useMudancaDasConclusoes(mesa.ccbs);
  const releituraDoMaterial = `${versao}:${conclusoesMudaram}`;
  const { ancora, aba, escolher } = useAbaDaMesa();
  const painel = usePainelDaMesa();
  const [saida, setSaida] = useSaidaDaMesa();
  // E203.F5.T07: uma leitura do pacote para a mesa (a prévia ao lado e o card do exame), relida
  // quando a mesa grava e quando as peças mudam.
  const releituraDoPacote = useMemo(() => ({ versao, ccbs: mesa.ccbs }), [versao, mesa.ccbs]);
  const pacote = usePreparacaoDoPacote(client, caseId, releituraDoPacote);
  // E206.F3 (GUS-296 item 4): o documento também se relê quando a preparação termina.
  const fimDaPreparacao = useFimDaPreparacao(pacote.leitura);
  const releituraDoDocumento = useMemo(
    () => ({ versao, fimDaPreparacao }),
    [versao, fimDaPreparacao],
  );
  const documento = useDocumentoDaSaida(client, caseId, saida, releituraDoDocumento);
  // E205.F3: uma leitura da prontidão para a aba Pendências, o contador dela e o chip do cabeçalho.
  const prontidao = useResultadoDoCaso(client, caseId, releituraDoPacote);
  const proximo = proximoQueBloqueia(prontidao.resultado);
  const pendentes = quantasPendencias(prontidao.resultado);
  const dadosDoPacote = pacote.leitura.estado === 'pronto' ? pacote.leitura.dados : null;
  // E209.F2.T09 (GUS-296 item 8): com a saída em falha, a pendência da composição leva ao "Retomar".
  const pendenciasNaMesa = useMemo(
    () => resultadoComASaidaQueFalhou(prontidao.resultado, dadosDoPacote),
    [prontidao.resultado, dadosDoPacote],
  );
  const comRisco =
    dadosDoPacote?.linhas.some((l) => l.chave === 'riscos' && l.estado !== 'nao_se_aplica') ??
    false;

  const recarregarMesa = mesa.recarregar;
  const mesaMudou = useCallback((): void => {
    void recarregarMesa();
    setVersao((v) => v + 1);
  }, [recarregarMesa]);
  const materialGravou = useCallback((): void => void recarregarMesa(), [recarregarMesa]);
  const edicao = useEdicaoNoDocumento(
    client,
    caseId,
    saida,
    documento.dados,
    { versaoSalva: mesaMudou, reler: documento.tentarDeNovo },
    dadosDoPacote,
  );
  const [busca] = useSearchParams();
  const dado = usePainelDoDadoDaMesa(documento.dados);
  const secao = useSecaoDoExame(documento.dados, saida, ancora, edicao);
  const trechos = documento.dados?.trechos ?? [];
  const notaDaSecao = (secao: string | null) => {
    const nota = trechos.find((x) => x.tipo === 'nota' && x.secao === secao);
    return nota === undefined || edicao === null ? null : () => edicao.abrir(nota.id);
  };
  const assinado = documento.dados?.assinado ?? true;
  // E203.F5.T02: "Protocolo e documentos" fica fora da tela até o "Mais" (ou `#protocolo`) pedir.
  const [protocoloAberto, setProtocoloAberto] = useState(ancora === 'protocolo');
  const [pedidoDeEnvio, setPedidoDeEnvio] = useState(0);
  // E203.F5.T08: o próximo passo chega por âncora; o protocolo e o envio abrem o bloco que o resolve.
  useEffect(() => {
    if (ancora === 'protocolo') setProtocoloAberto(true);
    if (ancora === 'adicionar-documentos') setPedidoDeEnvio((n) => n + 1);
  }, [ancora]);
  // E205.F4.T05: a ficha saiu da mesa; o endereço antigo (`#ficha-da-leitura`) leva a ela no "Mais".
  useEffect(() => {
    if (ancora !== ANCORA_DA_FICHA) return;
    const pathname = `/casos/${caseId}/informacoes-da-leitura`;
    navigate({ pathname, hash: ANCORA_DA_FICHA }, { replace: true });
  }, [ancora, caseId, navigate]);
  const dados = dadosDoCaso({
    caso: mesa.caso,
    bancoDoCaso: mesa.bancoDoCaso,
    documento: documento.dados,
    pacote: pacote.leitura,
  });

  return (
    <PainelDoDocumentoContexto.Provider value={painel.contexto}>
      <AbrirDadoContexto.Provider value={assinado ? null : dado.abrir}>
        <section
          className="pb-sc-8"
          data-testid="mesa-unica-page"
          aria-labelledby="mesa-unica-title"
        >
          <CabecalhoDaMesa
            caseId={caseId}
            caso={mesa.caso}
            ccbs={mesa.ccbs}
            dados={dados}
            proximo={
              proximo === null
                ? null
                : { passo: proximo.passo, onAbrir: () => escolher('pendencias') }
            }
            pendencia={pendenciaDoDocumento(edicao)}
            onEnviarDocumentos={() => {
              setPedidoDeEnvio((n) => n + 1);
              navigate({ hash: 'adicionar-documentos' });
            }}
            onProtocolo={() => {
              setProtocoloAberto(true);
              navigate({ hash: 'protocolo' });
            }}
          />
          {/* E193.F1 (GUS-259): o caso digital parado diz o que falta antes da análise. */}
          <ArquivosPendentes
            pendentes={mesa.caso?.arquivos_pendentes}
            texto={ptBR.arquivosPendentes.textoDaEquipe}
            testId="mesa-arquivos-pendentes"
            className="mt-sc-4"
          />
          <MesaEletronicaEstado
            erroGlobal={mesa.erroGlobal}
            ccbs={mesa.ccbs}
            onTentarDeNovo={() => void mesa.tentarDeNovo()}
            tentando={mesa.tentando}
          />

          <div
            className={`grid gap-sc-6 ${painel.aba === null ? '' : COLUNAS_COM_DOCUMENTO}`}
            data-testid="mesa-colunas"
          >
            <div className="min-w-0">
              <AbasDaMesa
                aba={aba}
                onAba={escolher}
                marcas={{ pendencias: <ContadorDePendencias n={pendentes} /> }}
              >
                {/* A área do material desenha os painéis de Documentos e de Fotografias. */}
                <PreAnaliseEletronicaPage
                  caseId={caseId}
                  releitura={releituraDoMaterial}
                  onGravou={materialGravou}
                  onProcessando={setProcessando}
                  protocoloAberto={protocoloAberto}
                  onFecharProtocolo={() => {
                    setProtocoloAberto(false);
                    navigate({ hash: 'documentos' });
                  }}
                  pedidoDeEnvio={pedidoDeEnvio}
                  fotografias={fotografias}
                  arquivosPendentes={mesa.caso?.arquivos_pendentes}
                />
                <PainelDaAba aba="exame">
                  <AreaDoExame
                    client={client}
                    caseId={caseId}
                    mesa={mesa}
                    processando={processando}
                    fotograficaEmAndamento={analiseFotograficaEmAndamento(fotografias.dados)}
                    pacote={pacote}
                    documento={{
                      leitura: documento,
                      saida,
                      comRisco,
                      onSaida: setSaida,
                      edicao,
                      apontado: busca.get('trecho'),
                    }}
                    secao={secao}
                    onMudou={mesaMudou}
                    painel={painel.aba}
                    onPainel={painel.alternar}
                    onPrevia={() => painel.mostrar('previa')}
                  />
                </PainelDaAba>
                <PainelDaAba aba="pendencias">
                  <PendenciasDaMesa
                    caseId={caseId}
                    resultado={pendenciasNaMesa}
                    onTentarDeNovo={prontidao.reler}
                  />
                </PainelDaAba>
                <PainelDaAba aba="conclusao">
                  <AreaDaConclusao
                    client={client}
                    caseId={caseId}
                    mesa={mesa}
                    onMudou={mesaMudou}
                    onAbrirDocumentos={() => {
                      setProtocoloAberto(true);
                      navigate({ hash: 'protocolo' });
                    }}
                    documento={documento.dados}
                  />
                </PainelDaAba>
              </AbasDaMesa>
            </div>
            {painel.aba !== null ? (
              <PainelDoDocumento
                client={client}
                caseId={caseId}
                aba={painel.aba}
                onAba={painel.alternar}
                aberto={painel.documento}
                onMudar={painel.setDocumento}
                onFechar={painel.fechar}
                previa={{
                  saida,
                  linha: dadosDoPacote?.linhas.find((l) => l.chave === saida),
                  preparando: dadosDoPacote?.preparando ?? false,
                }}
              />
            ) : null}
          </div>

          <PainelDoDadoDaMesa
            estado={dado}
            client={client}
            caseId={caseId}
            mesa={mesa}
            onMudou={mesaMudou}
            notaDaSecao={notaDaSecao}
          />
          <SairSemPerder
            edicao={edicao}
            trechos={trechos}
            painel={{ sujo: dado.sujo, descartar: dado.fechar }}
          />
        </section>
      </AbrirDadoContexto.Provider>
    </PainelDoDocumentoContexto.Provider>
  );
}
